/**
 * The board `···` ▸ **Notifications** dialog (`specs/M1-B10-shell-chrome.md` §4.4).
 *
 * monday's entry is a plain row, not a submenu (`[c:board-menu]` — hovering it opens nothing),
 * and what it shows is the per-board mute state M5.C1 already stores:
 * `NOT_MUTED` / `MENTIONS_AND_ASSIGNS_ONLY` / `MUTE_ALL`, the three the notification-settings
 * screen exposes as *Everything* / *Only mentions and assignments* / *Nothing*. The board-info
 * popover's read-only "Notifications: Everything" row reads the same value.
 */
import { useOverlayDismiss } from '@elizmonday/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTRPC, useTRPCClient } from '../../lib/trpc';
import { useBoardInvalidation } from './hooks';

/** The three states the board-level control offers; `CUSTOM_SETTINGS` is set per kind, not here. */
export const BOARD_MUTE_STATES = [
  { id: 'NOT_MUTED', label: 'Everything' },
  { id: 'MENTIONS_AND_ASSIGNS_ONLY', label: 'Only mentions and assignments' },
  { id: 'MUTE_ALL', label: 'Nothing' },
] as const;

export type BoardMuteState = (typeof BOARD_MUTE_STATES)[number]['id'];

export function boardMuteLabel(state: string | null | undefined): string {
  return BOARD_MUTE_STATES.find((option) => option.id === state)?.label ?? 'Everything';
}

export function BoardNotificationsDialog({
  boardId,
  boardName,
  onClose,
}: {
  boardId: string;
  boardName: string;
  onClose(): void;
}) {
  const trpc = useTRPC();
  const client = useTRPCClient();
  const invalidate = useBoardInvalidation();
  /** P-260: Escape closes it, and focus returns to the menu row that opened it. */
  useOverlayDismiss({ onClose });


  const mutes = useQuery({ ...trpc.notifications.boardMutes.queryOptions(), staleTime: 30_000 });
  const current =
    (mutes.data ?? []).find((row) => String(row.boardId) === String(boardId))?.muteState ?? 'NOT_MUTED';

  const save = useMutation({
    mutationFn: (muteState: BoardMuteState) =>
      client.notifications.setBoardMute.mutate({ boardId: Number(boardId), muteState, enabled: [] }),
    onSuccess: async () => {
      await mutes.refetch();
      await invalidate();
      onClose();
    },
  });

  return (
    <div className="ez-dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="ez-board-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={`Notifications for ${boardName}`}
        data-testid="board-notifications-dialog"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <h2 className="ez-board-dialog__title">Notifications</h2>
        <p className="ez-board-dialog__hint">{`What ${boardName} may notify you about.`}</p>
        <div role="radiogroup" aria-label="Board notifications">
          {BOARD_MUTE_STATES.map((option) => (
            <label key={option.id} className="ez-board-dialog__radio">
              <input
                type="radio"
                name="board-mute"
                checked={current === option.id}
                data-testid={`board-mute-${option.id}`}
                onChange={() => save.mutate(option.id)}
              />
              {option.label}
            </label>
          ))}
        </div>
        <footer className="ez-board-dialog__footer">
          <button type="button" className="ez-board-dialog__cancel" onClick={onClose}>
            Close
          </button>
        </footer>
      </div>
    </div>
  );
}
